import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateLoader } from '@cfp/core';
import { EnergyLabel } from '@cfp/shared/energy-label';
import {
  Badge,
  Card,
  CurrencyModule,
  Icon,
  InfoModalButton,
  PaybackPeriodModule,
  SortOrder,
  Table,
  TableColumn
} from '@cfp/ui';
import { LabelImpactBarPipe } from '@cfp/ui/pipes/label-impact-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  BuildingMeasureTableItemDTO,
  CalculatedEnergyLabelDTO,
  CurrencyCodeDTO,
  LabelGroupDTO,
  MeasureCodeDTO,
  RegisteredOrCalculatedEnergyLabelDTO
} from 'csr-asset-app';
import { map } from 'rxjs/operators';
import { BuildingReportRoadmapInterface } from './building-report-roadmap.interface';

@Component({
  templateUrl: 'building-report-roadmap-card.html',
  selector: 'cfp-building-report-roadmap-card',
  imports: [
    Table,
    Card,
    ReactiveFormsModule,
    TranslateModule,
    LabelImpactBarPipe,
    EnergyLabel,
    PaybackPeriodModule,
    CurrencyModule,
    Icon,
    InfoModalButton,
    Badge
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BuildingReportRoadmapCard {
  private readonly translateService = inject(TranslateService);
  private readonly translateLoader = inject(TranslateLoader);

  public readonly labelGroup = input.required<LabelGroupDTO | undefined>();
  public readonly rows = input<BuildingReportRoadmapInterface[]>([]);
  public readonly currencyCode = input<CurrencyCodeDTO | undefined>(undefined);
  public readonly currentEnergyLabel = input<RegisteredOrCalculatedEnergyLabelDTO | undefined>(undefined);
  public readonly improvedEnergyLabel = input<CalculatedEnergyLabelDTO | undefined>(undefined);
  public readonly showRecognisedMeasureCheck = model<boolean>(false);
  public readonly customRecognizedMeasureIcon = input<string | undefined>(undefined);
  public readonly measuresWithBadge = input<Record<MeasureCodeDTO, string> | undefined>(undefined);

  protected readonly SortOrder = SortOrder;

  protected readonly isTranslationLoaded = toSignal(
    this.translateLoader.loadTranslation('roadmap').pipe(map(() => true))
  );

  protected readonly columns = computed<TableColumn[]>(() => {
    if (!this.isTranslationLoaded()) {
      return [];
    }

    return [
      { key: 'year', name: 'year', noSort: true },
      { key: 'impactEnergyLabel', name: 'afterImplementationLabel' },
      { key: 'code', name: 'measure' },
      { key: 'investment', name: 'investment' },
      { key: 'validUntil', name: 'tvtYear' },
      { key: 'reductionPotential', name: 'reductionPotential' },
      {
        key: 'impactEnergyLabels',
        name: this.translateService.instant('impactEnergyLabel', {
          labelGroup: this.translateService.instant(`labelGroup.${this.labelGroup()}`)
        })
      }
    ];
  });

  constructor() {
    effect(() => {
      if (!this.showRecognisedMeasureCheck()) {
        return;
      }

      this.showRecognisedMeasureCheck.set(
        this.rows().some(
          (measure: BuildingReportRoadmapInterface) =>
            measure.measureList?.some((mesureItem: BuildingMeasureTableItemDTO) => mesureItem.recognized) || false
        )
      );
    });
  }

  protected mapToBuildingReportRoadmap(entry: any): BuildingReportRoadmapInterface {
    return entry as BuildingReportRoadmapInterface;
  }
}
